import { Component, inject, ViewContainerRef } from '@angular/core';
import { Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, take } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe, JsonPipe, NgTemplateOutlet, CommonModule } from '@angular/common';

import { AuthService } from '../shared/auth/auth.service';
import { ToggleSideNavService } from '../shared/toggle-side-nav.service';
import { TopActionsService } from './top-actions.service';
import { AichatService } from '../aichat.service';
import { SidemenuComponent } from '../sidemenu/sidemenu.component';
import { BreadcrumbNavigationComponent } from '../breadcrumb-navigation/breadcrumb-navigation.component';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { AichatComponent } from '../aichat/aichat.component';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { MessagingDropdownBellComponent } from '../messaging/messaging-dropdown-bell.component';
import { backendEndPointToken, authBackendEndPointToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';
import { User } from '../shared/auth/auth.service';
import { RealtimeAlertComponent } from '../shared/notification/realtime-alert/realtime-alert.component';
import { AppTitleService } from '../services/title.service';
import { UtilityService } from '../shared/utility.service';
import { RoleDisplayNamePipe } from '../shared/role-display-name.pipe';
import { NavigationService } from '../shared/auth/navigation.service';
import { HospitalSwitcherComponent } from '../remote-doctor/hospital-switcher.component';
import { InfoBannerComponent } from '../shared/info-banner/info-banner.component';
import { isPractitionerRole } from '../shared/system-roles';
import { FirebaseAlertService, RealtimeAlert } from '../shared/services/firebase-alert.service';


@Component({
  selector: 'app-app-wrapper',
  templateUrl: './app-wrapper.component.html',
  styleUrls: ['./app-wrapper.component.scss'],
  imports: [
    MatIconModule, MatSidenavModule, MatDividerModule,
    MatFormFieldModule, MatMenuModule, MatSelectModule, MatButtonModule,
    ReactiveFormsModule, AsyncPipe, JsonPipe, NgTemplateOutlet,
    CommonModule, HospitalSwitcherComponent,
    RouterOutlet, SidemenuComponent, BreadcrumbNavigationComponent,
    PatientNamePipe, AichatComponent,
    NotificationBellComponent,
    MessagingDropdownBellComponent,
    RealtimeAlertComponent,
    RoleDisplayNamePipe,
    InfoBannerComponent
  ]
})
export class AppWrapperComponent {
  // FormControl for role selection
  private titleService = inject(AppTitleService);


  userChangeForm = new FormControl<string>('');

  // Responsive breakpoint
  breakpointObserver = inject(BreakpointObserver);
  isBigTablet$ = this.breakpointObserver.observe(['(max-width: 992px)'])
    .pipe(map(result => result.matches));

  // Services
  sideNavService = inject(ToggleSideNavService);
  auth = inject(AuthService);
  topactionsService = inject(TopActionsService);
  aiServ = inject(AichatService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private navigationService = inject(NavigationService);
  vCRef = inject(ViewContainerRef);
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly authBackendUrl = inject(authBackendEndPointToken);
  firebaseAlerts = inject(FirebaseAlertService);

  // Component state
  collapseSideNav: boolean = this.sideNavService.sideNavCollapsed.getValue();
  currentUserRoles: string[] = [];
  orgWideDataLoaded = false;
  isDark = false;
  private avatarVersion = Date.now();
  patientHospitalId: string | null = null;
  showMissingScheduleBanner = false;
  activeEmergencyAlert: RealtimeAlert | null = null;
  proposedRoleSynonyms: Record<string, string> = {
        'remote doctor': 'remotedoctor',
        'health care assistant': 'healthcareassistant',
        'nurse / midwife': 'nursemidwife',
        'Allied Health Professional': 'consultant',
        'pharmacy': 'pharmacy',
        'Hospital Admin': 'admin'
      };
  ngOnInit() {
    this.titleService.setTitle('Dashboard - e-Likita');

    this.firebaseAlerts.emergencyAlerts$.subscribe(alert => {
      this.activeEmergencyAlert = alert;
    });

    this.route.data.subscribe((data: any) => {
      if (data['title']) {
        this.titleService.setTitle(`${data['title']} - e-Likita`);
      }
    });

    this.auth.user.pipe(take(1)).subscribe((user: any) => {
      if (user && user.role && isPractitionerRole(user.role)) {
        this.checkScheduleStatus();
      }
    });

    this.isDark = localStorage.getItem('color-scheme') === 'dark';
    this.applyTheme();

    // Listen to side nav collapse changes
    this.sideNavService.sideNavCollapsed.subscribe(e => this.collapseSideNav = e);

    // Listen to user changes and set up roles
    this.auth.user.subscribe(user => {
      if (!user) {
        this.currentUserRoles = [];
        this.userChangeForm.setValue('', { emitEvent: false });
        this.userChangeForm.disable({ emitEvent: false });
        this.patientHospitalId = null;
        return;
      }

      this.avatarVersion = Date.now();

      // Get user's roles (normalized to lowercase in auth.service)
      this.currentUserRoles = user.roles || [];
      // for now, add to user roles
    
      const currentRole = user.role; // The active role

      console.log('User roles:', this.currentUserRoles);
      console.log('Current active role:', currentRole);

      // Set the form control to the current active role
      if (this.currentUserRoles.length === 0) {
        this.userChangeForm.setValue('', { emitEvent: false });
        this.userChangeForm.disable({ emitEvent: false });
      } else if (this.currentUserRoles.length === 1) {
        // Only one role - set it and disable
        this.userChangeForm.setValue(this.currentUserRoles[0], { emitEvent: false });
        this.userChangeForm.disable({ emitEvent: false });
      } else {
        // Multiple roles - set current active role and enable
        this.userChangeForm.setValue(currentRole, { emitEvent: false });
        this.userChangeForm.enable({ emitEvent: false });
      }

      this.resolvePatientIdentifier(user);
    });

    // Update active role when user selects from dropdown
   this.userChangeForm.valueChanges.subscribe(selectedRole => {
  if (!selectedRole) return;
  this.auth.setActiveRole(selectedRole).subscribe(success => {
    if (success) {
      console.log('Active role changed to:', selectedRole);
      this.handleRoleChangeNavigation(selectedRole);
    }
  });
});

    // Listen for route data
    this.route.data.subscribe(({ orgWide }) => this.orgWideDataLoaded = !!orgWide);
  }
  getRoleDisplay(): string {
    const user = this.auth.user.getValue();
    const specialty = this.auth.getCurrentUserSpecialty();

    const value = specialty || user?.role || '';

    return value
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : '';
  }

  getUserDisplayName(): string {
    const user = this.auth.user.getValue();
    const fullName = user?.name ?? '';

    // If name is short enough, return it
    if (fullName.length <= 15) {
      return fullName;
    }

    // Name is too long - extract initials and surname
    const nameParts = fullName.trim().split(' ');

    if (nameParts.length > 1) {
      // Get all parts except the last (surname)
      const firstNames = nameParts.slice(0, -1);
      const surname = nameParts[nameParts.length - 1];

      // Create initials from first names (e.g., "M.B")
      const initials = firstNames
        .map(name => name.charAt(0).toUpperCase() + '.')
        .join(' ');

      // Return format like "M.B Ribadu"
      return `${initials} ${surname}`;
    }

    // If no spaces (single word name), truncate with ellipsis
    return fullName.substring(0, 17) + '...';
  }


  openandC() {
    const v = !this.aiServ.openAndC.getValue();
    this.aiServ.openAndC.next(v);
  }

  dismissEmergencyAlert() {
    if (this.activeEmergencyAlert?.path && this.activeEmergencyAlert?.id) {
      this.firebaseAlerts.markAsSeen(this.activeEmergencyAlert.path, this.activeEmergencyAlert.id);
    }
    this.firebaseAlerts.clearEmergencyAlert();
  }

  toggleTheme() {
    this.isDark = !this.isDark;
    localStorage.setItem('color-scheme', this.isDark ? 'dark' : 'light');
    this.applyTheme();
  }

  private applyTheme() {
    document.documentElement.setAttribute('color-scheme', this.isDark ? 'dark' : 'light');
  }

 switchRole(role: string) {
  if (!role) return;
  let normalizedRole = role.toLowerCase();
  if (this.proposedRoleSynonyms[role]) {
    normalizedRole = this.proposedRoleSynonyms[role];
  }
  this.auth.setActiveRole(normalizedRole).subscribe(success => {
    if (success) {
      this.userChangeForm.setValue(normalizedRole, { emitEvent: false });
      this.handleRoleChangeNavigation(normalizedRole);
    }
  });
}

  private handleRoleChangeNavigation(role: string): void {
    const currentUrl = this.router.url;
    if (!this.navigationService.isRouteAllowedForRole(currentUrl, role)) {
      console.warn(`Current route ${currentUrl} not allowed for role ${role}. Redirecting to role default page...`);
      this.navigationService.navigateToRoleDefaultPage(role);
    }
  }

  openProfile() {
    void this.router.navigate(['/app/profile']);
  }

  logout() {
    this.auth.logout();
  }

  getThemeIcon(): string {
    return this.isDark ? 'light_mode' : 'dark_mode';
  }

  getThemeLabel(): string {
    return this.isDark ? 'Switch to light mode' : 'Switch to dark mode';
  }
get canUseMessaging(): boolean {
  const user = this.auth.user.getValue();
  return !!(user?.organizationId ?? user?.hospitalId);
}
  get canSwitchRoles(): boolean {
    return this.currentUserRoles.length > 1;
  }
  utility = inject(UtilityService);

  getCurrentUserPhotoUrl(): string {
    const photoUrl = this.auth.user.getValue()?.photo?.[0]?.url;
    if (!photoUrl) {
      return 'https://www.dummyimg.in/placeholder';
    }

    const separator = photoUrl.includes('?') ? '&' : '?';
    return `${photoUrl}${separator}v=${this.avatarVersion}`;
  }

  private resolvePatientIdentifier(user: User): void {
    if ((user.role || '').toLowerCase() !== 'patient') {
      this.patientHospitalId = null;
      return;
    }

    const cachedIdentifier = this.utility.getPatientHospitalId(user) || this.utility.getPatientHospitalId((user as any)?.fhirResource);
    if (cachedIdentifier) {
      this.patientHospitalId = cachedIdentifier;
      return;
    }

    const patientId = user.fhirResourceId || (user as any)?.fhirId || user.id;
    if (!patientId) {
      this.patientHospitalId = null;
      return;
    }

    const context = new HttpContext().set(LoadingUIEnabled, false);

    this.http.get<any>(`${this.backendUrl}/Patient/${patientId}`, { context })
      .pipe(take(1))
      .subscribe({
        next: patient => {
          this.patientHospitalId = this.utility.getPatientHospitalId(patient);
        },
        error: () => {
          this.patientHospitalId = null;
        }
      });
  }

  private extractIdentifierFromResource(resource: any): string | null {
    if (!resource) {
      return null;
    }

    const identifiers = resource.identifier || resource.identifiers;
    if (!Array.isArray(identifiers)) {
      return null;
    }

    const normalized = identifiers
      .map(identifier => this.normalizeIdentifier(identifier))
      .filter((entry): entry is { value: string; system: string; type: string; assigner: string } => !!entry && !!entry.value);

    if (normalized.length === 0) {
      return null;
    }

    const prioritized = normalized.find(entry => this.looksLikeHospitalIdentifier(entry));
    return (prioritized ?? normalized[0]).value;
  }

  private normalizeIdentifier(identifier: any): { value: string; system: string; type: string; assigner: string } | null {
    if (!identifier) {
      return null;
    }

    const value = this.extractIdentifierValue(identifier);
    if (!value) {
      return null;
    }

    const system = this.toLower(identifier.system);
    const type = this.toLower(
      identifier.type?.text ||
      identifier.type?.coding?.[0]?.display ||
      identifier.type?.coding?.[0]?.code
    );
    const assigner = this.toLower(identifier.assigner?.display || identifier.assigner?.reference);

    return { value, system, type, assigner };
  }

  private extractIdentifierValue(identifier: any): string | null {
    if (typeof identifier.value === 'string') {
      const trimmed = identifier.value.trim();
      return trimmed || null;
    }

    if (identifier.value && typeof identifier.value.value === 'string') {
      const embedded = identifier.value.value.trim();
      return embedded || null;
    }

    return null;
  }

  private toLower(value: any): string {
    return typeof value === 'string' ? value.toLowerCase() : '';
  }

  private looksLikeHospitalIdentifier(identifier: { system: string; type: string; assigner: string }): boolean {
    const haystack = `${identifier.system} ${identifier.type} ${identifier.assigner}`;
    return haystack.includes('hospital') || haystack.includes('mrn') || haystack.includes('facility') || haystack.includes('elikita');
  }

  checkScheduleStatus() {
    // Determine the target month based on whether today is past the 15th
    const today = new Date();
    const targetDate = new Date();
    
    if (today.getDate() > 15) {
      targetDate.setMonth(targetDate.getMonth() + 1);
    }
    
    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
    
    this.http.get<{ isComplete: boolean }>(`${this.authBackendUrl}/api/schedules/status?month=${yyyy}-${mm}`)
      .subscribe({
        next: (res) => {
          if (res && res.isComplete === false) {
            this.showMissingScheduleBanner = true;
          }
        },
        error: (err) => console.error('Failed to check schedule status', err)
      });
  }

  dismissScheduleBanner() {
    this.showMissingScheduleBanner = false;
  }
}
